/**
 * InlineAddButton — dashed-border pill with a circular `+` glyph.
 * The affordance is "this field is intentionally empty, click to
 * add it inline." Pairs with the completion-strip pattern: visible
 * gaps rather than hidden ones, so the user knows what's missing
 * without a checklist somewhere else.
 *
 * Used in:
 *   · Customer cover · empty Contact cell (`Add primary contact`,
 *     `WhatsApp number`)
 *   · Customer detail side rail · empty Pinned-note card
 *   · CompletionStrip · `Add ↗` trailing action on todo rows
 *
 * On hover the dashed border solidifies into a solid sky outline
 * with a faint sky background — telegraphs "this is a real button,
 * not just a placeholder."
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface InlineAddButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> {
  /** Button copy. Imperative voice — `"Add primary contact"` not `"Primary contact"`. */
  label: string;
}

export function InlineAddButton({ label, className, ...props }: InlineAddButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1",
        "rounded-md border border-dashed border-line-2 bg-transparent",
        "text-sky-700 text-xs font-semibold",
        "transition-colors duration-100",
        "hover:border-solid hover:border-sky-600 hover:bg-sky-50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-200",
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className={cn(
          "inline-grid place-items-center w-3.5 h-3.5 rounded-full",
          "bg-sky-600 text-paper text-xs font-bold leading-none",
        )}
      >
        +
      </span>
      <span>{label}</span>
    </button>
  );
}
